import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Search,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Filter,
  UserPlus,
} from "lucide-react";
import { getPrebookingsAPI, assignBatchToPrebookingAPI } from "../services/apicollection";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PrebookingPaymentPage = () => {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [batches, setBatches] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  const [assignBatchId, setAssignBatchId] = useState(null);

  const fetchData = useCallback(
    async (page = pagination.page) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPrebookingsAPI({
          page,
          limit: pagination.limit,
          status: statusFilter,
          search: searchDebounced || undefined,
        });
        if (res?.status === false) {
          throw new Error(res.message || "Failed to load data");
        }
        const payload = res?.data || res;
        setItems(Array.isArray(payload.items) ? payload.items : []);
        setPagination((prev) => ({
          ...prev,
          page: payload.pagination?.page ?? page,
          total: payload.pagination?.total ?? 0,
          totalPages: payload.pagination?.totalPages ?? 0,
        }));
      } catch (err) {
        setError(err.message || "Failed to load prebookings");
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, searchDebounced, pagination.limit]
  );

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    fetchData(1);
  }, [statusFilter, searchDebounced, fetchData]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchData(newPage);
  };

  const getBatchesForPrebooking = (item) => {
    if (!batches.length) return [];
    const courseId = item.courseId;
    const courseName = (item.course || "").toLowerCase().trim();
    const byCourse = batches.filter(
      (b) =>
        (courseId && String(b.courseId) === courseId) ||
        (courseName && (b.courseName || "").toLowerCase().trim() === courseName)
    );
    return byCourse.length > 0 ? byCourse : batches;
  };

  const handleAssignBatch = async (prebookingId, batchName) => {
    if (!batchName) return;
    setAssigningId(prebookingId);
    try {
      const res = await assignBatchToPrebookingAPI(prebookingId, batchName);
      if (res?.status === false) {
        throw new Error(res.message || "Failed to assign batch");
      }
      setAssignBatchId(null);
      await fetchData(pagination.page);
    } catch (err) {
      setError(err.message || "Failed to assign batch");
    } finally {
      setAssigningId(null);
    }
  };

  const tableRows = items.map((row) => ({
    fullname: row.fullname || "—",
    email: row.email || "—",
    number: row.number || "—",
    course: row.course || "—",
    "Assigned Batch": row.booking?.batch || "—",
    "Course Mode": row.booking?.mode || "—",
    amount: row.payment?.amount != null ? `₹${row.payment.amount}` : "—",
    orderId: row.payment?.orderId || row.paymentTracking?.razorpayOrderId || "—",
    razorpayPaymentId:
      row.payment?.paymentId || row.paymentTracking?.razorpayPaymentId || "—",
    paymentStatus: (
      <span
        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
          (row.payment?.paymentStatus || row.paymentTracking?.paymentStatus) === "paid"
            ? "bg-emerald-50 text-emerald-700"
            : (row.payment?.paymentStatus || row.paymentTracking?.paymentStatus) ===
                "failed"
              ? "bg-red-50 text-red-700"
              : "bg-amber-50 text-amber-700"
        }`}
      >
        {(row.payment?.paymentStatus || row.paymentTracking?.paymentStatus) || "pending"}
      </span>
    ),
    paidAt: formatDate(row.payment?.paidAt || row.paymentTracking?.paidAt),
    createdAt: formatDate(row.createdAt),
    Actions: null,
  }));

  const columns = tableRows.length > 0 ? Object.keys(tableRows[0]) : [];

  const renderActionsCell = (row, idx) => {
    const item = items[idx];
    const availableBatches = getBatchesForPrebooking(item);
    const isAssigning = assigningId === item._id;
    const isExpanded = assignBatchId === item._id;

    return (
      <div className="flex items-center gap-2">
        {availableBatches.length > 0 ? (
          <>
            {!isExpanded ? (
              <button
                type="button"
                onClick={() => setAssignBatchId(isExpanded ? null : item._id)}
                disabled={isAssigning}
                className="inline-flex items-center gap-1 rounded-lg border border-[#1d3350] px-2 py-1.5 text-xs font-medium text-slate-300 hover:bg-[#0d1b2e] disabled:opacity-50"
                title="Assign batch"
              >
                <UserPlus size={14} />
                Assign batch
              </button>
            ) : (
              <div className="flex flex-col gap-1 min-w-[180px]">
                <select
                  className="rounded bg-[#0d1220] text-slate-100 border border-[#1d3350] px-2 py-1.5 text-xs"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) handleAssignBatch(item._id, val);
                  }}
                  disabled={isAssigning}
                  defaultValue=""
                >
                  <option value="">Select batch...</option>
                  {availableBatches.map((b) => (
                    <option key={b._id} value={b.batchName}>
                      {b.batchName} ({b.mode || "offline"})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setAssignBatchId(null)}
                  className="text-xs text-slate-500 hover:text-slate-300"
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        ) : (
          <span className="text-xs text-slate-400">No batches</span>
        )}
        {isAssigning && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">
          Prebooking & Payment Tracking
        </h2>
      </div>

      <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e] p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search by Order ID or Razorpay Payment ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-lg border border-[#1d3350] py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
            >
              <option value="all">All status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : tableRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-slate-500">
            <CreditCard size={40} className="text-slate-300" />
            <p className="mt-2 text-sm">
              {searchInput || statusFilter !== "all"
                ? "No records match your filters."
                : "No prebookings yet."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead className="bg-[#0d1b2e] border-b border-[#1d3350]">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col}
                        className={`px-4 py-3 text-xs font-semibold  first-letter:uppercase tracking-wide text-slate-400 ${col === "Actions" ? "sticky right-0 bg-[#0d1b2e] shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.05)]" : ""}`}
                      >
                        {col.replace(/([A-Z])/g, " $1").replace(/_/g, " ").trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tableRows.map((row, idx) => (
                    <tr
                      key={items[idx]?._id || idx}
                      className="hover:bg-[#0d1b2e]/80 transition-colors"
                    >
                      {columns.map((col) => (
                        <td
                          key={col}
                          className={`px-4 py-3 text-sm text-slate-300 whitespace-nowrap ${col === "Actions" ? "sticky right-0 bg-[#0f1c2e] shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.05)]" : ""}`}
                        >
                          {col === "Actions"
                            ? renderActionsCell(row, idx)
                            : typeof row[col] === "object" && row[col] !== null
                              ? row[col]
                              : row[col]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-[#1d3350] pt-4">
                <p className="text-sm text-slate-400">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total}{" "}
                  total)
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-[#0d1b2e] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-[#0d1b2e] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PrebookingPaymentPage;